import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ButtonBase,
  Chip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import { ReactFlowProvider } from "reactflow";
import ClusterTopology from "./ClusterTopology.jsx";
import { clusterSupabase } from "../../../../components/SupabaseClient.js";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK_SECONDARY,
  INK_MUTED,
  SURFACE,
} from "../../../../theme/tokens.js";

const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
// RADIUS.md (12px) via the sx scale (theme shape = 8px → 1.5 × 8).
const cardSx = { p: 2, borderRadius: 1.5, border: `1px solid ${HAIRLINE}` };

// Small uppercase muted section label (matches the left nav style)
function SectionLabel({ children, component = "div" }) {
  return (
    <Typography
      component={component}
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: INK_MUTED,
      }}
    >
      {children}
    </Typography>
  );
}

// Server / Switch pill, colored to match the canvas legend
function TypeChip({ type }) {
  const isSwitch = type === "switch";
  return (
    <Chip
      label={isSwitch ? "Switch" : "Server"}
      size="small"
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        color: isSwitch ? ACCENT_DARK : INK_SECONDARY,
        bgcolor: isSwitch ? ACCENT_TINT : "#F1F5F9",
        border: `1px solid ${isSwitch ? ACCENT : HAIRLINE_STRONG}`,
      }}
    />
  );
}

// One aligned key/value row inside the detail grid
function DetailRow({ label, value, mono, muted }) {
  return (
    <>
      <Typography component="span" sx={{ fontSize: 12, color: INK_MUTED }}>
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 13,
          color: muted ? "text.secondary" : "text.primary",
          fontFamily: mono ? MONO : undefined,
          wordBreak: "break-all",
        }}
      >
        {value || "—"}
      </Typography>
    </>
  );
}

// Format a device's rack-unit span, e.g. "12–14" or "5"
function formatRu(details) {
  if (!details || details.rackPosRu == null) return null;
  const h = details.heightRu || 1;
  return h > 1
    ? `${details.rackPosRu}–${details.rackPosRu + h - 1}`
    : String(details.rackPosRu);
}

export default function ClusterTopologyPage() {
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [recenterRequest, setRecenterRequest] = useState(0);

  // Pods list & selected Pod
  const [pods, setPods] = useState([]);
  const [selectedPodId, setSelectedPodId] = useState("");

  // Racks list & selected Rack
  const [racksInPod, setRacksInPod] = useState([]);
  const [selectedRackId, setSelectedRackId] = useState("");

  // Whether selecting a node should highlight all related links
  const [highlightRelatedEdges, setHighlightRelatedEdges] = useState(true);

  // Focus the canvas on a rack (keeps the selection so the dropdown reflects it)
  const focusOnRack = (rackId) => {
    setSelectedRackId(rackId);
    setSelectedNode(null);
  };

  const selectConnectedDevice = (device) => {
    if (!device?.rack_id) return;
    const rack = racksInPod.find(
      (r) => String(r.rack_id) === String(device.rack_id),
    );
    const role = device.role || null;
    const normalizedRole = role?.toLowerCase() || "";
    const type =
      normalizedRole.includes("switch") || normalizedRole.includes("tor")
        ? "switch"
        : "server";

    setSelectedRackId(device.rack_id);
    setSelectedNode({
      id: `dev-${device.device_id}`,
      type,
      data: {
        label: device.hostname || `Device ${device.device_id}`,
        details: {
          id: device.device_id,
          ip: device.ip || device.properties?.ip || null,
          role,
          rackName: rack?.rack_name || `Rack ${device.rack_id}`,
          rackPosRu: device.rack_pos_ru ?? null,
          heightRu: device.height_ru ?? null,
        },
      },
    });
    setRecenterRequest((request) => request + 1);
  };

  const handleNodeSelect = useCallback((node) => {
    // Only server or switch nodes are shown in the details panel;
    // anything else (null / edges / racks / podWires) clears it.
    if (node?.type === "server" || node?.type === "switch") {
      setSelectedNode(node);
    } else {
      setSelectedNode(null);
    }
  }, []);

  // Load Pods table
  useEffect(() => {
    async function fetchPods() {
      try {
        const { data, error } = await clusterSupabase.rpc("get_pods");
        if (error) throw error;
        setPods(data || []);
      } catch (e) {
        console.error("Failed to load Pods:", e);
      }
    }
    fetchPods();
  }, []);

  // Fetch racks when selected pod changes
  useEffect(() => {
    async function fetchRacksInPod(podId) {
      if (!podId) {
        setRacksInPod([]);
        return;
      }
      try {
        const { data, error } = await clusterSupabase.rpc("get_racks_by_pod", {
          in_pod_id: podId,
        });
        if (error) throw error;
        setRacksInPod(data || []);
      } catch (e) {
        console.error("Failed to load racks for pod:", e);
        setRacksInPod([]);
      }
    }

    fetchRacksInPod(selectedPodId);
    setSelectedRackId("");
    setSelectedNode(null);
  }, [selectedPodId]);

  // Default select the first pod when pods are loaded
  useEffect(() => {
    if (pods.length > 0 && (selectedPodId === "" || selectedPodId == null)) {
      setSelectedPodId(pods[0].pod_id);
    }
  }, [pods, selectedPodId]);

  // Fetch connected devices when a node is selected
  useEffect(() => {
    async function fetchConnectedDevices(deviceId) {
      if (!deviceId) {
        setConnectedDevices([]);
        return;
      }
      try {
        const { data, error } = await clusterSupabase.rpc(
          "get_connected_devices",
          {
            in_device_id: deviceId,
          },
        );
        if (error) throw error;
        setConnectedDevices(data || []);
      } catch (e) {
        console.error("Failed to load connected devices:", e);
        setConnectedDevices([]);
      }
    }

    if (selectedNode) {
      fetchConnectedDevices(selectedNode.data.details.id);
    } else {
      setConnectedDevices([]);
    }
  }, [selectedNode]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/*=========== Main Content ===========*/}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 0,
          gap: 2, // spacing between canvas and right panel
          px: 2, // left/right padding
          pb: { xs: 2, md: 0 },
        }}
      >
        {/*=========== Left topology canvas ===========*/}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 380, md: 0 },
            position: "relative",
            bgcolor: SURFACE,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          {selectedPodId ? (
            <ReactFlowProvider>
              <ClusterTopology
                selectedPodId={Number(selectedPodId)}
                selectedRackId={selectedRackId}
                selectedNodeId={selectedNode?.id || null}
                recenterRequest={recenterRequest}
                highlightRelatedEdges={highlightRelatedEdges}
                onHighlightChange={setHighlightRelatedEdges}
                onNodeSelect={handleNodeSelect}
              />
            </ReactFlowProvider>
          ) : (
            <Box
              sx={{
                p: 4,
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Please select a Pod on the right to visualize its racks and
              servers.
            </Box>
          )}
        </Box>

        {/*=========== Right Sidebar ===========*/}
        <Box
          sx={{
            width: { xs: "100%", md: 320 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* ===== Scope: Pod + Rack (improved) ===== */}
          <Paper elevation={0} sx={cardSx}>
            <SectionLabel>Scope</SectionLabel>
            <FormControl fullWidth size="small" sx={{ mt: 1.25 }}>
              <InputLabel>Pod</InputLabel>
              <Select
                label="Pod"
                value={selectedPodId}
                onChange={(e) => {
                  setSelectedPodId(e.target.value);
                  setSelectedNode(null);
                }}
              >
                {pods.map((p) => (
                  <MenuItem key={p.pod_id} value={p.pod_id}>
                    {p.pod_name || `Pod ${p.pod_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              disabled={!selectedPodId}
            >
              <InputLabel>Rack</InputLabel>
              <Select
                label="Rack"
                value={selectedRackId}
                onChange={(e) => focusOnRack(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Racks</em>
                </MenuItem>
                {[...racksInPod]
                  .sort((a, b) =>
                    (a.rack_name || `Rack ${a.rack_id}`).localeCompare(
                      b.rack_name || `Rack ${b.rack_id}`,
                    ),
                  )
                  .map((r) => (
                    <MenuItem key={r.rack_id} value={r.rack_id}>
                      {r.rack_name || `Rack ${r.rack_id}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Paper>

          {/* ===== Device Detail (improved) ===== */}
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SectionLabel>Device Detail</SectionLabel>
            {selectedNode ? (
              <Box
                sx={{
                  mt: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedNode.data.label}
                  </Typography>
                  <TypeChip type={selectedNode.type} />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    columnGap: 2,
                    rowGap: 0.75,
                  }}
                >
                  <DetailRow
                    label="IP"
                    value={selectedNode.data.details.ip}
                    mono
                  />
                  <DetailRow
                    label="Role"
                    value={selectedNode.data.details.role}
                  />
                  <DetailRow
                    label="Rack"
                    value={selectedNode.data.details.rackName}
                  />
                  <DetailRow
                    label="RU"
                    value={formatRu(selectedNode.data.details)}
                  />
                  <DetailRow
                    label="ID"
                    value={selectedNode.data.details.id}
                    mono
                    muted
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                    mb: 0.75,
                  }}
                >
                  <SectionLabel component="span">Connected</SectionLabel>
                  <Chip
                    size="small"
                    label={connectedDevices.length}
                    sx={{ height: 18, fontSize: 11, fontWeight: 600 }}
                  />
                </Box>
                <Box
                  sx={{
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 1.5,
                    flex: 1,
                    maxHeight: 420,
                    overflowY: "auto",
                  }}
                >
                  {connectedDevices.length > 0 ? (
                    connectedDevices.map((dev) => {
                      const clickable = Boolean(dev.rack_id);
                      return (
                        <ButtonBase
                          key={dev.device_id}
                          onClick={() => selectConnectedDevice(dev)}
                          disabled={!clickable}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            textAlign: "left",
                            px: 1,
                            py: 0.75,
                            "&:hover": { backgroundColor: "action.hover" },
                            "&.Mui-disabled": { opacity: 0.6 },
                          }}
                        >
                          <LanOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: INK_MUTED,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            title={dev.hostname || `Device ${dev.device_id}`}
                            sx={{
                              fontSize: 13,
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dev.hostname || `Device ${dev.device_id}`}
                          </Typography>
                          {clickable && (
                            <ChevronRightIcon
                              sx={{
                                fontSize: 16,
                                color: INK_MUTED,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </ButtonBase>
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: 13, px: 1, py: 0.75 }}
                    >
                      No direct connections.
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  py: 4,
                  gap: 1,
                }}
              >
                <LanOutlinedIcon sx={{ fontSize: 28, color: INK_MUTED }} />
                <Typography variant="body2" sx={{ fontSize: 13 }}>
                  Select a node to see details
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
